import * as PIXI from 'pixi.js';
import { Config } from '../../../video-data/config';
import type { UpdateFlowMixinSettings } from '../../updateFlowMixin';
import { ReactiveGraphic } from './ReactiveGraphic';
import {
    AlphaAttribute,
    ColorAttribute,
    NumberAttribute,
    StringAttribute,
    getAttributesToSerialize,
} from '../../attributes';
import { int2rgba } from '../../../libs/videoData';
import { createLinearGradient } from '../../../libs/gradient';
import { clampCornerRadii } from '../../../libs/roundedRectPath';
import { GradientPoint } from '../../GradientPoint';
import type { GradientStop } from '../../GradientPoint';
import type { DeepReadonlyObject, DestroyOptions } from '../../../types';
import { comparer, observable, reaction } from '../../reactivity';

export type BoxGraphicAnimationXDirection = (typeof Config.ALL_ANIMATION_IN_X_DIRECTION)[number];
export type BoxGraphicAnimationYDirection = (typeof Config.ALL_ANIMATION_IN_Y_DIRECTION)[number];

type BoxGraphicState = DeepReadonlyObject<
    Pick<
        BoxGraphic,
        | 'positionX'
        | 'positionY'
        | 'boxWidth'
        | 'boxHeight'
        | 'topLeftRadius'
        | 'topRightRadius'
        | 'bottomRightRadius'
        | 'bottomLeftRadius'
        | 'paddingTop'
        | 'paddingBottom'
        | 'paddingLeft'
        | 'paddingRight'
        | 'strokeColor'
        | 'strokeAlpha'
        | 'strokeWidth'
        | 'fillColor'
        | 'fillAlpha'
        | 'progressYValue'
        | 'progressXValue'
        | 'horizontalDirection'
        | 'verticalDirection'
        | 'fillType'
    > & { gradientStart: GradientStop; gradientEnd: GradientStop }
>;

/**
 * Draws a colored bounding box around the entire text(Metrics)
 *
 */
export class BoxGraphic extends ReactiveGraphic {
    override _classType = 'BoxGraphic';
    override _name = Config.BOX_GRAPHIC;

    @NumberAttribute({ animatable: false })
    @observable
    accessor positionX = 150;
    @NumberAttribute({ animatable: false })
    @observable
    accessor positionY = 150;
    @NumberAttribute()
    @observable
    accessor boxWidth = 50;
    @NumberAttribute()
    @observable
    accessor boxHeight = 50;
    @NumberAttribute({ min: 0, animatable: false })
    @observable
    accessor topLeftRadius = 0;
    @NumberAttribute({ min: 0, animatable: false })
    @observable
    accessor topRightRadius = 0;
    @NumberAttribute({ min: 0, animatable: false })
    @observable
    accessor bottomRightRadius = 0;
    @NumberAttribute({ min: 0, animatable: false })
    @observable
    accessor bottomLeftRadius = 0;
    @NumberAttribute({ animatable: false })
    @observable
    accessor paddingTop = 0;
    @NumberAttribute({ animatable: false })
    @observable
    accessor paddingBottom = 0;
    @NumberAttribute({ animatable: false })
    @observable
    accessor paddingLeft = 0;
    @NumberAttribute({ animatable: false })
    @observable
    accessor paddingRight = 0;
    @ColorAttribute()
    @observable
    accessor strokeColor = 16777215;
    @AlphaAttribute()
    @observable
    accessor strokeAlpha = 1;
    @NumberAttribute({ min: 0, animatable: false })
    @observable
    accessor strokeWidth = 0;
    @ColorAttribute()
    @observable
    accessor fillColor = 16777215;
    @AlphaAttribute()
    @observable
    accessor fillAlpha = 1;
    @NumberAttribute({ min: 0 })
    @observable
    accessor progressYValue: number = 1.0;
    @NumberAttribute({ min: 0 })
    @observable
    accessor progressXValue = 1.0;
    @StringAttribute({ choices: Config.ALL_ANIMATION_IN_X_DIRECTION })
    @observable
    accessor horizontalDirection: BoxGraphicAnimationXDirection = Config.RIGHT_ANIMATION_DIRECTION;
    @StringAttribute({ choices: Config.ALL_ANIMATION_IN_Y_DIRECTION })
    @observable
    accessor verticalDirection: BoxGraphicAnimationYDirection = Config.CENTER_ANIMATION_DIRECTION;

    @StringAttribute({ choices: ['solid', 'linear'], animatable: false })
    @observable
    accessor fillType: 'solid' | 'linear' = 'solid';

    gradientStartPoint = new GradientPoint({ parent: this, x: 0, y: 0.5, color: 0xff0000 });
    gradientEndPoint = new GradientPoint({ parent: this, x: 1, y: 0.5, color: 0x0000ff });

    #canvas: HTMLCanvasElement | null = null;
    #gradientTexture: PIXI.Texture | null = null;
    #gradientTextureKey: string | null = null;
    #renderedGraphicsState: BoxGraphicState | null = null;

    constructor(settings: UpdateFlowMixinSettings) {
        super(settings);
        this.addReaction(
            reaction(
                () => this.#graphicsState(),
                (state) => this.#applyGraphicsState(state),
                { name: 'BoxGraphic.graphicsState' },
            ),
        );
    }

    #graphicsState(): BoxGraphicState {
        return {
            positionX: this.positionX,
            positionY: this.positionY,
            boxWidth: this.boxWidth,
            boxHeight: this.boxHeight,
            topLeftRadius: this.topLeftRadius,
            topRightRadius: this.topRightRadius,
            bottomRightRadius: this.bottomRightRadius,
            bottomLeftRadius: this.bottomLeftRadius,
            paddingTop: this.paddingTop,
            paddingBottom: this.paddingBottom,
            paddingLeft: this.paddingLeft,
            paddingRight: this.paddingRight,
            strokeColor: this.strokeColor,
            strokeAlpha: this.strokeAlpha,
            strokeWidth: this.strokeWidth,
            fillColor: this.fillColor,
            fillAlpha: this.fillAlpha,
            progressYValue: this.progressYValue,
            progressXValue: this.progressXValue,
            horizontalDirection: this.horizontalDirection,
            verticalDirection: this.verticalDirection,
            fillType: this.fillType,
            gradientStart: this.gradientStartPoint._toStop(),
            gradientEnd: this.gradientEndPoint._toStop(),
        };
    }

    #applyGraphicsState(state: BoxGraphicState) {
        if (comparer.structural(state, this.#renderedGraphicsState)) {
            return;
        }
        const { x, y, width, height } = BoxGraphic.#calculateDimensions(state);
        const { topLeftRadius, topRightRadius, bottomLeftRadius, bottomRightRadius } =
            BoxGraphic.#calculateShape(state, width, height);
        this.#drawShape(
            state,
            x,
            y,
            width,
            height,
            topLeftRadius,
            topRightRadius,
            bottomLeftRadius,
            bottomRightRadius,
        );
        this.#renderedGraphicsState = state;
    }

    static #calculateDimensions(state: BoxGraphicState) {
        const totalHeight = state.boxHeight + state.paddingBottom + state.paddingTop;
        const totalWidth = state.boxWidth + state.paddingLeft + state.paddingRight;
        const percentageHeight = totalHeight * state.progressYValue;
        const percentageWidth = totalWidth * state.progressXValue;
        let positionX = state.positionX;
        let positionY = state.positionY;

        switch (state.horizontalDirection) {
            case 'right':
                // pass
                break;
            case 'left':
                positionX += (1 - state.progressXValue) * totalWidth;
                break;
            case 'center':
                positionX += (totalWidth - percentageWidth) / 2;
                break;
            default:
                throw Error(`Unknown ${state.horizontalDirection}`);
        }

        switch (state.verticalDirection) {
            case 'bottom':
                // pass
                break;
            case 'top':
                positionY += (1 - state.progressYValue) * totalHeight;
                break;
            case 'center':
                positionY += (totalHeight - percentageHeight) / 2;
                break;
            default:
                throw Error(`Unknown ${state.verticalDirection}`);
        }

        return {
            x: positionX,
            y: positionY,
            width: percentageWidth,
            height: percentageHeight,
        };
    }

    static #calculateShape(state: BoxGraphicState, width: number, height: number) {
        return clampCornerRadii(
            {
                topLeftRadius: state.topLeftRadius,
                topRightRadius: state.topRightRadius,
                bottomRightRadius: state.bottomRightRadius,
                bottomLeftRadius: state.bottomLeftRadius,
            },
            width,
            height,
        );
    }

    #createGradientTexture(start: GradientStop, end: GradientStop, width: number, height: number) {
        if (!this.#canvas) {
            this.#canvas = document.createElement('canvas');
        }
        const shapeWidth = Math.max(1, Math.round(width));
        const shapeHeight = Math.max(1, Math.round(height));

        const key = [
            shapeWidth,
            shapeHeight,
            start.x,
            start.y,
            start.color,
            start.alpha,
            end.x,
            end.y,
            end.color,
            end.alpha,
        ].join(':');
        if (this.#gradientTexture && this.#gradientTextureKey === key) {
            return this.#gradientTexture;
        }

        // +1 px padding: UV at the shape edge becomes width/(width+1) < 1.0,
        // preventing wrap-around artifacts regardless of the texture's wrapMode.
        const canvasWidth = shapeWidth + 1;
        const canvasHeight = shapeHeight + 1;
        this.#canvas.width = canvasWidth;
        this.#canvas.height = canvasHeight;

        const context = this.#canvas.getContext('2d');
        if (!context) return null;

        context.clearRect(0, 0, canvasWidth, canvasHeight);

        const gradient = createLinearGradient(context, {
            startX: start.x * shapeWidth,
            startY: start.y * shapeHeight,
            endX: end.x * shapeWidth,
            endY: end.y * shapeHeight,
            startStop: int2rgba(start.color, start.alpha),
            endStop: int2rgba(end.color, end.alpha),
        });

        context.fillStyle = gradient;
        context.fillRect(0, 0, canvasWidth, canvasHeight);

        if (this.#gradientTexture) {
            this.#gradientTexture.destroy(true);
        }
        this.#gradientTexture = PIXI.Texture.from(this.#canvas);
        this.#gradientTextureKey = key;
        return this.#gradientTexture;
    }

    #drawShape(
        state: BoxGraphicState,
        x: number,
        y: number,
        width: number,
        height: number,
        topLeftRadius: number,
        topRightRadius: number,
        bottomLeftRadius: number,
        bottomRightRadius: number,
    ) {
        this.clear();
        this.lineStyle(state.strokeWidth, state.strokeColor, state.strokeAlpha);

        const fillColor = state.fillType === 'solid' ? state.fillColor : 0xffffff;
        const fillAlpha = state.fillType === 'solid' ? state.fillAlpha : 1;

        if (state.fillType === 'linear') {
            const texture = this.#createGradientTexture(
                state.gradientStart,
                state.gradientEnd,
                width,
                height,
            );
            if (texture) {
                // beginTextureFill inverts the matrix before applying it, so pass
                // the positive offset (PIXI inverts to -x, -y) to align texture top-left
                // with the shape's top-left corner.
                const matrix = new PIXI.Matrix();
                matrix.translate(x, y);
                this.beginTextureFill({ texture, matrix });
            } else {
                this.beginFill(fillColor, fillAlpha);
            }
        } else {
            this.beginFill(fillColor, fillAlpha);
        }

        // draw a simple rectangle if no rounded corners are required
        if (
            topLeftRadius === 0 &&
            topRightRadius === 0 &&
            bottomRightRadius === 0 &&
            bottomLeftRadius === 0
        ) {
            this.drawRect(x, y, width, height);
            this.endFill();
            return;
        }

        // draw rectangle with one or more rounded corners
        if (topLeftRadius < 1) {
            this.lineTo(x, y);
        } else {
            this.arc(x + topLeftRadius, y + topLeftRadius, topLeftRadius, Math.PI, -Math.PI / 2);
        }

        if (topRightRadius < 1) {
            this.lineTo(x + width, y);
        } else {
            this.arc(
                x + width - topRightRadius,
                y + topRightRadius,
                topRightRadius,
                -Math.PI / 2,
                0,
            );
        }

        if (bottomRightRadius < 1) {
            this.lineTo(x + width, y + height);
        } else {
            this.arc(
                x + width - bottomRightRadius,
                y + height - bottomRightRadius,
                bottomRightRadius,
                0,
                Math.PI / 2,
            );
        }

        if (bottomLeftRadius < 1) {
            this.lineTo(x, y + height);
        } else {
            this.arc(
                x + bottomLeftRadius,
                y + height - bottomLeftRadius,
                bottomLeftRadius,
                Math.PI / 2,
                Math.PI,
            );
        }
        this.closePath();
        this.endFill();
    }

    protected override _calculateBounds() {
        this.#applyGraphicsState(this.#graphicsState());
        super._calculateBounds();
    }

    override get propertiesToSerialize(): Array<string> {
        return getAttributesToSerialize(this).concat(['gradientStartPoint', 'gradientEndPoint']);
    }

    override destroy(options?: DestroyOptions) {
        if (this.#gradientTexture) {
            this.#gradientTexture.destroy(true);
            this.#gradientTexture = null;
            this.#gradientTextureKey = null;
        }
        super.destroy(options);
    }
}
